import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ContractVehicleDropdown from './ContractVehicleDropdown'
import ContractDropdown from './ContractDropdown'
import { ContractShape, ContractVehicleShape, ContractVehiclesShape, SupplierShape } from './prop_shapes/PropShapes'

const SelectContract = ({ contractVehicles, contract, contractVehicle, supplier, freeform, inputSelectors }) => {
  useEffect(() => {
    $(document).foundation()
  })

  const prefillData = () => {
    let prefilledContract = null
    let prefilledCV = null

    // if both are provided, ensure contract is a part of contract vehicle
    if (contract && contractVehicle) {
      if (contractVehicle.contracts.some(c => c.id === contract.id)) {
        prefilledCV = contractVehicle
        prefilledContract = contract
      }
    // if only contract is provided, find parent contract vehicle
    } else if (contract) {
      const parentCV = contractVehicles.find(cv => cv.contracts.some(c => c.id === contract.id))
      if (parentCV) {
        prefilledCV = parentCV
        prefilledContract = contract
      }
    // if only contract vehicle is provided, then set it
    } else if (contractVehicle) {
      prefilledCV = contractVehicle
    }

    return { prefilledCV, prefilledContract }
  }

  const { prefilledCV, prefilledContract } = prefillData()

  const checkIfPrefilled = () => {
    return !!(prefilledCV || prefilledContract) && supplier
  }

  const [chosenSupplier, setChosenSupplier] = useState(supplier)
  const [selectableContractVehicles, setSelectableContractVehicles] = useState(contractVehicles)
  const [addingContract, setAddingContract] = useState(checkIfPrefilled())
  const [selectedContractVehicle, setSelectedContractVehicle] = useState(prefilledCV)
  const [selectedContract, setSelectedContract] = useState(prefilledContract)
  const [freeformContract, setFreeformContract] = useState(freeform)
  const [notListed, setNotListed] = useState(false)

  useEffect(() => {
    $(inputSelectors.contract).val(`${selectedContract?.id || ''}`).change()
    $(inputSelectors.contractVehicle).val(`${selectedContractVehicle?.id || ''}`).change()
    $(inputSelectors.contractFreeform).val(`${freeformContract}`).change()
  }, [selectedContract, selectedContractVehicle, freeformContract])

  // ----- BEGIN Handle when chosen supplier changes -----

  const setChosenSupplierData = () => {
    const { supplier, contractVehicles } = $('#chosen-supplier-data').data()
    setSelectableContractVehicles(contractVehicles)
    setChosenSupplier(supplier)
    handleRemoveContract()
  }

  const listenForChosenSupplierChange = () => {
    return new MutationObserver(() => setChosenSupplierData())
      .observe(document.getElementById('chosen-supplier-data'), { attributes: true })
  }

  useEffect(() => {
    const observer = listenForChosenSupplierChange()
    return () => {
      observer?.disconnect()
    }
  }, [])

  // ----- END Handle when chosen supplier changes -----

  const handleInitialClick = () => {
    setAddingContract(true)
  }

  const handleRemoveContract = () => {
    setAddingContract(false)
    setSelectedContractVehicle(null)
    setSelectedContract(null)
    setFreeformContract('')
    setNotListed(false)
  }

  const renderLabel = () => {
    return (
      <div className='label-title'>
        <label className='sub-header'>Contract</label>
        {addingContract ? <a onClick={handleRemoveContract} id='remove-contract-link'>Remove Contract</a> : null}
      </div>
    )
  }

  const renderInitialPrompt = () => {
    const disabled = (chosenSupplier == null) || (selectableContractVehicles.length === 0)
    return (
      <button id='add-prompt' className={disabled ? 'disabled' : null} onClick={disabled ? null : handleInitialClick} disabled={disabled}>
        <span className='fa-stack add-prompt-icon'>
          <i className='fa-stack-2x fa-thin fa-circle' />
          <i className='fa-stack-1x fa-solid fa-plus' />
        </span>
        <span className='add-prompt-text'>
          <strong>Add Contract Information</strong> (Optional)
        </span>
      </button>
    )
  }

  const handleAddContractVehicleClick = (cv) => {
    setSelectedContractVehicle(cv)
    setSelectedContract(null)
    setFreeformContract('')
    $('#contract-vehicle-dropdown').foundation('close')
  }

  const handleAddContractClick = (contract) => {
    setSelectedContract(contract)
    setFreeformContract('')
    $('#contract-dropdown').foundation('close')
  }

  const handleNotListedClick = () => {
    setNotListed(true)
    setSelectedContract(null)
  }

  const handleFreeformContract = (event) => {
    setFreeformContract(event.target.value)
  }

  const handleUnselectContract = () => {
    setSelectedContract(null)
    $('#contract-dropdown').foundation('close')
  }

  const handleUnselectContractVehicle = () => {
    setSelectedContractVehicle(null)
    setSelectedContract(null)
    $('#contract-vehicle-dropdown').foundation('close')
  }

  const renderDropdownSelects = () => {
    return (
      <>
        <ContractVehicleDropdown
          contractVehicles={selectableContractVehicles}
          selectedContractVehicle={selectedContractVehicle}
          handleAddContractVehicleClick={handleAddContractVehicleClick}
          handleUnselectContractVehicle={handleUnselectContractVehicle}
        />
        <ContractDropdown
          selectedContract={selectedContract}
          selectedContractVehicle={selectedContractVehicle}
          freeformContract={freeformContract}
          notListed={notListed}
          handleFreeformContract={handleFreeformContract}
          handleNotListedClick={handleNotListedClick}
          handleAddContractClick={handleAddContractClick}
          handleUnselectContract={handleUnselectContract}
        />
      </>
    )
  }

  return (
    <div id='link-contract-review'>
      {renderLabel()}
      {addingContract ? renderDropdownSelects() : renderInitialPrompt()}
    </div>
  )
}

SelectContract.propTypes = {
  contractVehicles: ContractVehiclesShape.isRequired,
  contract: ContractShape,
  contractVehicle: ContractVehicleShape,
  supplier: SupplierShape,
  freeform: PropTypes.string,
  inputSelectors: PropTypes.shape({
    contractVehicle: PropTypes.string,
    contract: PropTypes.string,
    contractFreeform: PropTypes.string
  })
}

SelectContract.defaultProps = {
  contractVehicles: [],
  contract: null,
  contractVehicle: null,
  freeform: null,
  supplier: null,
  inputSelectors: {
    contractVehicle: '#review_contract_vehicle_id',
    contract: '#review_contract_id',
    contractFreeform: '#review_contract_freeform'
  }
}

export default SelectContract
